import React, { Fragment, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { DownloadIcon, YaySmallIcon } from 'secondstep-components'
import NavigationHelper from 'layers/navigation/navigationHelper'
import { isFlagOn } from 'utils/featureFlags'
import { toTitleCase } from 'utils/stringHelpers'
import NavigatorBubbleButton from './NavigatorBubbleButton'
import NavigatorButton from './NavigatorButton'
import {
  BubbleNavigatorWrapper,
  DateText,
  Description,
  EditButton,
  EstimatedTime,
  GetStartedButton,
  GoalContentWrapper,
  GoalContentHeaderWrapper,
  GoalCompletedText,
  GoalSubTitle,
  GoalText,
  GoalTitleWrapper,
  Image,
  PageContent,
  PageNavigator,
  PlaybookStepsWrapper,
  RightContent,
  StepDropdown,
  StepNumber,
  StyledDateRange,
  StyledEditIcon,
  StyledGoalHr,
  StyledHr,
  StyledLinkDropdown,
  StyledProgressBar,
  SubTitle,
  SummaryLink,
  TextContent,
  Title,
  TopWrapper,
} from './component.styles'
import {
  DROPDOWN_DEFAULT_LABEL,
  DROPDOWN_NAME,
  GET_STARTED_BUTTON_TEXT,
  GET_STARTED_BUTTON_TEST_ID,
  GOALS_TEXT,
  HOST_TEXT,
  MARK_COMPLETE_BUTTON_TEXT,
  PAGE_CONTENT_TEST_ID,
  PURPOSE_TEXT,
  RESUME_BUTTON_TEXT,
  SET_GOAL_TEST_ID,
  SUMMARY_TEXT,
} from './constants'
import {
  dropdownOptions,
  goalNavigationData,
  modalMap,
  stepDataMap,
} from './data'

const PlaybookSteps = ({
  adminSetK8PlaybookStepsDropdown,
  classCreationGoalValue,
  error,
  familyLetterSent,
  fetchK8PlaybookData,
  goal1Date,
  goal1IsCompleted,
  goal2Date,
  goal2IsCompleted,
  goal3Date,
  goal3IsCompleted,
  goal3Text,
  goal4Date,
  goal4IsCompleted,
  goal4Text,
  goal5Date,
  goal5IsCompleted,
  goal5Text,
  goalsAreCompleted,
  goalsIsInDraft,
  implementationApproachIsCompleted,
  isFetching,
  kickoffHosted,
  kickoffPrepared,
  lessonCompletionGoalValue,
  linkDropdownDisplayTitle,
  linkMenu,
  navigationEvent,
  panelData,
  playbookData,
  programStartDate,
  programStartDateIsCompleted,
  purposeIsCompleted,
  purposeIsInDraft,
  isSingleSite,
  showMonitorViewOnly,
  siteId,
  stateDropdownOption,
  userEmail,
  userInvitationMethod,
}) => {
  const [activePanel, setActivePanel] = useState(null)
  const [activeStep, setActiveStep] = useState(0)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [ModalComponent, setModalComponent] = useState(null)
  const [selectedDropdownOption, setSelectedDropdownOption] = useState(
    stateDropdownOption || dropdownOptions[0],
  )
  const [selectedGoal, setSelectedGoal] = useState('goal 1')
  const [showSummary, setShowSummary] = useState(false)
  const [sitePlaybookData, setSitePlaybookData] = useState({})
  const [currentPlanStep, setCurrentPlanStep] = useState(0)
  const [localDropdownOptions, setLocalDropdownOptions] = useState([
    ...dropdownOptions,
  ])

  const defaultLabel = selectedDropdownOption
    ? selectedDropdownOption.name
    : DROPDOWN_DEFAULT_LABEL

  const dropdownLinkNavigation = link => {
    const { download = null, openSameTab, url: optionUrlPath } = link || {}

    if (download) {
      navigationEvent(
        download?.file?.file?.url,
        NavigationHelper.types.DOWNLOAD_LINK,
      )
    }

    const type = openSameTab
      ? NavigationHelper.types.PUSH
      : NavigationHelper.types.NEW_TAB
    navigationEvent(optionUrlPath, type)
  }

  const handleActiveStep = () => {
    const stepConditions = {
      plan: [
        () => !implementationApproachIsCompleted,
        () => !goalsAreCompleted,
        () => !purposeIsCompleted,
        () => !programStartDateIsCompleted,
      ],
      launch: [
        () => !userInvitationMethod,
        () => !kickoffPrepared,
        () => !kickoffHosted,
        () => !familyLetterSent,
      ],
    }

    const conditions = stepConditions[selectedDropdownOption?.key] || []
    const newStep = conditions.findIndex(condition => condition())
    setActiveStep(newStep > 0 ? newStep : 0)
  }

  const handleGoalNavigation = goalKey => {
    setSelectedGoal(goalKey)
  }

  const isGoalDefined = goalKey => {
    const optionalGoalIsDefinedProps = {
      goal3IsCompleted,
      goal4IsCompleted,
      goal5IsCompleted,
    }
    const goalData = goalNavigationData.find(goal => goal.key === goalKey)
    if (!goalData) return false

    if (!goalData.isOptional) return true

    return goalData.isCompletedProp
      ? optionalGoalIsDefinedProps[goalData.isCompletedProp]
      : true
  }

  const launchModal = modalName => {
    const Modal = modalMap[modalName]?.component
    if (Modal) {
      setModalComponent(() => Modal)
      setIsModalOpen(true)
    }
  }

  const handleModalClose = () => {
    if (selectedDropdownOption?.key === 'plan') {
      const planSteps = stepDataMap.plan
      const nextStepIndex = currentPlanStep + 1

      if (nextStepIndex < Object.keys(planSteps).length) {
        setCurrentPlanStep(nextStepIndex)
        const nextStep = planSteps[nextStepIndex]
        launchModal(nextStep.modalName)
        return
      }
    }

    setIsModalOpen(false)
    setModalComponent(null)
  }

  const onChangeDropdown = e => {
    const optionToSelect = localDropdownOptions.find(
      option => option.key === e.key,
    )
    setSelectedDropdownOption(optionToSelect)
    if (optionToSelect.key === 'plan') {
      setCurrentPlanStep(0)
    }
    adminSetK8PlaybookStepsDropdown({ [siteId]: optionToSelect })
    handleActiveStep()
  }

  const setButtonText = modalName => {
    let buttonText = GET_STARTED_BUTTON_TEXT

    if (modalName === GOALS_TEXT && goalsIsInDraft) {
      buttonText = RESUME_BUTTON_TEXT
    }
    if (modalName === PURPOSE_TEXT && purposeIsInDraft) {
      buttonText = RESUME_BUTTON_TEXT
    }
    if (modalName === HOST_TEXT) {
      buttonText = MARK_COMPLETE_BUTTON_TEXT
    }

    return buttonText
  }

  const renderGoalNotStarted = () => {
    const panelData = activePanel?.modalFlows[selectedGoal.slice(-1) - 1]
    return (
      <PageContent showMonitorViewOnly={showMonitorViewOnly}>
        {panelData?.image?.file?.url && (
          <Image
            alt={panelData?.image?.title}
            src={panelData?.image?.file?.url}
          />
        )}
        <TextContent>
          <Title>{panelData?.shortHeader}</Title>
          <Description>{panelData?.description}</Description>
        </TextContent>
        <GetStartedButton
          dataTestId={SET_GOAL_TEST_ID}
          onClick={() => launchModal(GOALS_TEXT)}
        >
          {panelData?.buttonLabel}
        </GetStartedButton>
      </PageContent>
    )
  }

  const renderGoalCompleted = (goalDate, goalText) => {
    const isGoal1 = selectedGoal === 'goal 1'
    const isRequiredGoal = isGoal1 || selectedGoal === 'goal 2'
    const endDateInMs = new Date(goalDate).getTime()
    const endDate = new Date(goalDate)
    const endDateTextFormatted = endDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    })
    const goalTitle = toTitleCase(selectedGoal)
    const programStartDateInMs = new Date(programStartDate).getTime()
    const startDateTextFormatted = new Date(
      programStartDateInMs,
    ).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    })
    const classesCreatedPercentage = Math.round(
      (sitePlaybookData?.classes_created / classCreationGoalValue) * 100,
    )

    return (
      <Fragment key="goal-content-completed">
        <GoalContentWrapper showMonitorViewOnly={showMonitorViewOnly}>
          <GoalContentHeaderWrapper>
            <Title>{goalTitle}</Title>
            {(isGoal1 || (!isGoal1 && !programStartDate)) && (
              <DateText>Target Date: {endDateTextFormatted}</DateText>
            )}
            {!isGoal1 && programStartDate && (
              <StyledDateRange
                endDateInMs={endDateInMs}
                endDateTextFormatted={endDateTextFormatted}
                progressBarView={true}
                startDateInMs={programStartDateInMs}
                startDateTextFormatted={startDateTextFormatted}
              />
            )}

            <EditButton
              dataTestId={GET_STARTED_BUTTON_TEST_ID}
              onClick={() => launchModal(GOALS_TEXT)}
            >
              <StyledEditIcon />
            </EditButton>
          </GoalContentHeaderWrapper>
          <StyledGoalHr showMonitorViewOnly={showMonitorViewOnly} />
          {isRequiredGoal && (
            <Fragment key="goal-content-completed">
              <GoalTitleWrapper>
                <GoalSubTitle>
                  {isGoal1 ? 'Class Creation' : 'Lesson Completion'}
                </GoalSubTitle>
                <GoalCompletedText>
                  {isGoal1
                    ? sitePlaybookData?.classes_created
                    : sitePlaybookData?.lessons_marked_done}{' '}
                  of{' '}
                  {isGoal1
                    ? classCreationGoalValue
                    : sitePlaybookData?.total_lessons}{' '}
                  {isGoal1 ? 'classes' : 'lessons'}
                </GoalCompletedText>
              </GoalTitleWrapper>
              <StyledProgressBar
                goalPercentage={
                  !isGoal1 ? lessonCompletionGoalValue : undefined
                }
                goalValue={isGoal1 ? classCreationGoalValue : undefined}
                hideProgressText={true}
                percentComplete={
                  isGoal1
                    ? classesCreatedPercentage
                    : sitePlaybookData?.schoolwide_lesson_completion_percentage *
                      100
                }
                showGoalIndicator={true}
              />
            </Fragment>
          )}
          {!isRequiredGoal && (
            <Fragment key="custom-goal-content">
              <GoalSubTitle>Custom Goal</GoalSubTitle>
              <GoalText>{goalText}</GoalText>
            </Fragment>
          )}
        </GoalContentWrapper>
      </Fragment>
    )
  }

  const renderGoalContent = () => {
    const goalData = activePanel?.modalFlows?.find(
      modal => modal.header.toLowerCase() === selectedGoal,
    )

    if (selectedGoal === 'goal 1') {
      return goal1IsCompleted
        ? renderGoalCompleted(goal1Date)
        : renderGoalNotStarted()
    }
    if (selectedGoal === 'goal 2') {
      return goal2IsCompleted
        ? renderGoalCompleted(goal2Date)
        : renderGoalNotStarted()
    }
    if (selectedGoal === 'goal 3') {
      return goal3IsCompleted
        ? renderGoalCompleted(goal3Date, goal3Text)
        : renderGoalNotStarted()
    }
    if (selectedGoal === 'goal 4') {
      return goal4IsCompleted
        ? renderGoalCompleted(goal4Date, goal4Text)
        : renderGoalNotStarted()
    }
    if (selectedGoal === 'goal 5') {
      return goal5IsCompleted
        ? renderGoalCompleted(goal5Date, goal5Text)
        : renderGoalNotStarted()
    }

    return (
      <Fragment key="goal-content">
        <Image alt={goalData?.image?.title} src={goalData?.image?.file?.url} />
        <TextContent>
          <Title>{goalData?.shortHeader}</Title>
          <SubTitle>{goalData?.description}</SubTitle>
        </TextContent>
        <GetStartedButton
          dataTestId={GET_STARTED_BUTTON_TEST_ID}
          onClick={() => {
            setCurrentPlanStep(2)
            launchModal(GOALS_TEXT)
          }}
        >
          {goalData?.buttonLabel}
        </GetStartedButton>
      </Fragment>
    )
  }
  useEffect(() => {
    if (!error && !isFetching && !playbookData) {
      fetchK8PlaybookData(userEmail, siteId)
    }
  }, [error, isFetching, playbookData])

  useEffect(() => {
    const showMonitorDropdownFlagIsOn = isFlagOn(['show-monitor-dropdown'])
    const shouldRemoveMonitor =
      (!showMonitorDropdownFlagIsOn || !isSingleSite) && !showMonitorViewOnly

    if (shouldRemoveMonitor) {
      setLocalDropdownOptions(prev =>
        prev.filter(option => option.key !== 'monitor'),
      )
    } else {
      if (!stateDropdownOption) {
        const monitorOption = dropdownOptions.find(
          option => option.key === 'monitor',
        )
        if (monitorOption) {
          setSelectedDropdownOption(monitorOption)
          adminSetK8PlaybookStepsDropdown({ [siteId]: monitorOption })
        }
      }
      setLocalDropdownOptions([...dropdownOptions])
    }
  }, [
    error,
    isFetching,
    playbookData,
    isSingleSite,
    selectedDropdownOption,
    showMonitorViewOnly,
    stateDropdownOption,
  ])

  useEffect(() => {
    if (panelData?.length > 0) {
      const panel = panelData.find(({ header }) =>
        header.toLowerCase().includes(selectedDropdownOption?.key),
      )
      setActivePanel(panel)
    }
  }, [panelData, selectedDropdownOption, selectedGoal])

  useEffect(() => {
    const playbookDataBySiteId =
      playbookData?.find(site => site.site_id === siteId) ||
      playbookData?.[0] ||
      {}
    setSitePlaybookData(playbookDataBySiteId)
  }, [playbookData, siteId])

  useEffect(() => {
    if (
      selectedDropdownOption?.key === 'plan' &&
      goalsAreCompleted &&
      implementationApproachIsCompleted &&
      programStartDateIsCompleted &&
      purposeIsCompleted
    ) {
      setShowSummary(true)
      setActiveStep(4)
    } else if (
      selectedDropdownOption?.key === 'launch' &&
      familyLetterSent &&
      kickoffPrepared &&
      kickoffHosted &&
      userInvitationMethod
    ) {
      setShowSummary(true)
      setActiveStep(4)
    } else {
      setShowSummary(false)
      handleActiveStep()
    }
  }, [
    activeStep,
    familyLetterSent,
    goalsAreCompleted,
    implementationApproachIsCompleted,
    kickoffHosted,
    kickoffPrepared,
    programStartDateIsCompleted,
    purposeIsCompleted,
    selectedDropdownOption,
    showSummary,
    userInvitationMethod,
  ])

  useEffect(() => {
    if (showMonitorViewOnly) {
      const monitorOption = dropdownOptions.find(
        option => option.key === 'monitor',
      )
      if (monitorOption) {
        setSelectedDropdownOption(monitorOption)
        setSelectedGoal(goalNavigationData[0].key)
      }
    }
  }, [showMonitorViewOnly])

  useEffect(() => {
    // Reset to Goal 1 if currently selected optional goal has been removed
    if (selectedGoal && !isGoalDefined(selectedGoal)) {
      setSelectedGoal(goalNavigationData[0].key)
    }
  }, [selectedGoal, goal3IsCompleted, goal4IsCompleted, goal5IsCompleted])

  return (
    <PlaybookStepsWrapper showMonitorViewOnly={showMonitorViewOnly}>
      {!showMonitorViewOnly && (
        <TopWrapper>
          <StepDropdown
            dataTestId="playbook-steps-dropdown"
            defaultLabel={defaultLabel}
            keyProp="key"
            labelProp="name"
            name={DROPDOWN_NAME}
            onChange={onChangeDropdown}
            options={localDropdownOptions}
            selectOption={selectedDropdownOption}
            value={selectedDropdownOption}
          />
        </TopWrapper>
      )}
      {selectedDropdownOption?.key === 'monitor' && (
        <BubbleNavigatorWrapper>
          {goalNavigationData.map(goal => {
            if (!isGoalDefined(goal.key)) return null

            return (
              <NavigatorBubbleButton
                dataTestId={`goal-button-${goal.key}`}
                isActive={selectedGoal === goal.key}
                key={goal.key}
                onClick={() => handleGoalNavigation(goal.key)}
                showMonitorViewOnly={showMonitorViewOnly}
              >
                {goal.label}
              </NavigatorBubbleButton>
            )
          })}
        </BubbleNavigatorWrapper>
      )}
      {selectedDropdownOption?.key === 'monitor' && renderGoalContent()}
      {selectedDropdownOption?.key !== 'monitor' && (
        <PageContent dataTestId={PAGE_CONTENT_TEST_ID}>
          {!showSummary && selectedDropdownOption?.key !== 'monitor' && (
            <Fragment key="not-complete-step">
              {activePanel?.modalFlows[activeStep]?.image?.file?.url && (
                <Image
                  alt={activePanel?.modalFlows[activeStep]?.image?.title}
                  src={activePanel?.modalFlows[activeStep]?.image?.file?.url}
                />
              )}
              <TextContent>
                <StepNumber>
                  {activePanel?.modalFlows[activeStep]?.subhead}
                </StepNumber>
                <Title>
                  {activePanel?.modalFlows[activeStep]?.shortHeader}
                </Title>
                <EstimatedTime>
                  {activePanel?.modalFlows[activeStep]?.duration}
                </EstimatedTime>
              </TextContent>
              <GetStartedButton
                dataTestId={GET_STARTED_BUTTON_TEST_ID}
                onClick={() => {
                  if (selectedDropdownOption?.key === 'plan') {
                    setCurrentPlanStep(activeStep)
                  }
                  launchModal(
                    stepDataMap?.[selectedDropdownOption?.key]?.[activeStep]
                      ?.modalName,
                  )
                }}
              >
                {setButtonText(
                  stepDataMap?.[selectedDropdownOption?.key]?.[activeStep]
                    ?.modalName,
                )}
              </GetStartedButton>
            </Fragment>
          )}
          {showSummary && selectedDropdownOption?.key === 'plan' && (
            <Fragment key="complete-step">
              <YaySmallIcon id="step-icon" />
              <TextContent>
                <Title>You're on your way!</Title>
                <SubTitle>
                  Download and complete the customizable action plan and prepare
                  for launch.
                </SubTitle>
              </TextContent>
              <RightContent>
                {linkMenu && (
                  <StyledLinkDropdown
                    className="download-action-plan-button"
                    defaultLabel={linkDropdownDisplayTitle}
                    dropdownButtonIcon={<DownloadIcon />}
                    key="link-dropdown"
                    keyProp="id"
                    labelProp="buttonText"
                    onChange={link => dropdownLinkNavigation(link)}
                    options={linkMenu}
                    shouldHideSelectedIcon={true}
                  />
                )}
                <SummaryLink
                  onClick={() => {
                    if (selectedDropdownOption?.key === 'plan') {
                      setCurrentPlanStep(4)
                    }
                    launchModal(SUMMARY_TEXT)
                  }}
                >
                  {SUMMARY_TEXT}
                </SummaryLink>
              </RightContent>
            </Fragment>
          )}
          {showSummary && selectedDropdownOption?.key === 'launch' && (
            <Fragment key="complete-step">
              <YaySmallIcon id="step-icon" />
              <TextContent>
                <Title>You've launched the program!</Title>
                <SubTitle>
                  Use the Launch Snapshot to make sure teachers are getting
                  started.
                </SubTitle>
              </TextContent>
            </Fragment>
          )}
        </PageContent>
      )}
      {selectedDropdownOption?.key !== 'monitor' && (
        <PageNavigator>
          <NavigatorButton
            isActive={activeStep === 0}
            isCompleted={
              selectedDropdownOption?.key === 'plan'
                ? implementationApproachIsCompleted
                : !!userInvitationMethod
            }
            label={stepDataMap?.[selectedDropdownOption?.key]?.[0]?.label}
            onClick={() => {
              if (selectedDropdownOption?.key === 'plan') {
                setCurrentPlanStep(0)
              }
              launchModal(
                stepDataMap?.[selectedDropdownOption?.key]?.[0]?.modalName,
              )
            }}
          >
            {stepDataMap?.[selectedDropdownOption?.key]?.[0]?.label}
          </NavigatorButton>
          <StyledHr />
          <NavigatorButton
            isActive={activeStep === 1}
            isCompleted={
              selectedDropdownOption?.key === 'plan'
                ? goalsAreCompleted
                : !!kickoffPrepared
            }
            label={stepDataMap?.[selectedDropdownOption?.key]?.[1]?.label}
            onClick={() => {
              if (selectedDropdownOption?.key === 'plan') {
                setCurrentPlanStep(1)
              }
              launchModal(
                stepDataMap?.[selectedDropdownOption?.key]?.[1]?.modalName,
              )
            }}
          >
            {stepDataMap?.[selectedDropdownOption?.key]?.[1]?.label}
          </NavigatorButton>
          <StyledHr />
          <NavigatorButton
            isActive={activeStep === 2}
            isCompleted={
              selectedDropdownOption?.key === 'plan'
                ? purposeIsCompleted
                : !!kickoffHosted
            }
            label={stepDataMap?.[selectedDropdownOption?.key]?.[2]?.label}
            onClick={() => {
              if (selectedDropdownOption?.key === 'plan') {
                setCurrentPlanStep(2)
              }
              launchModal(
                stepDataMap?.[selectedDropdownOption?.key]?.[2]?.modalName,
              )
            }}
          >
            {stepDataMap?.[selectedDropdownOption?.key]?.[2]?.label}
          </NavigatorButton>
          <StyledHr />
          <NavigatorButton
            isActive={activeStep === 3}
            isCompleted={
              selectedDropdownOption?.key === 'plan'
                ? programStartDateIsCompleted
                : !!familyLetterSent
            }
            label={stepDataMap?.[selectedDropdownOption?.key]?.[3]?.label}
            onClick={() => {
              if (selectedDropdownOption?.key === 'plan') {
                setCurrentPlanStep(3)
              }
              launchModal(
                stepDataMap?.[selectedDropdownOption?.key]?.[3]?.modalName,
              )
            }}
          >
            {stepDataMap?.[selectedDropdownOption?.key]?.[3]?.label}
          </NavigatorButton>
        </PageNavigator>
      )}
      {isModalOpen && ModalComponent && (
        <ModalComponent
          handleCancel={() => {
            setIsModalOpen(false)
          }}
          handleClose={handleModalClose}
          siteId={siteId}
        />
      )}
    </PlaybookStepsWrapper>
  )
}

PlaybookSteps.propTypes = {
  adminSetK8PlaybookStepsDropdown: PropTypes.func,
  classCreationGoalValue: PropTypes.number,
  error: PropTypes.object,
  familyLetterSent: PropTypes.bool,
  fetchK8PlaybookData: PropTypes.func,
  goal1Date: PropTypes.number,
  goal1IsCompleted: PropTypes.bool,
  goal2Date: PropTypes.number,
  goal2IsCompleted: PropTypes.bool,
  goal3Date: PropTypes.number,
  goal3IsCompleted: PropTypes.bool,
  goal3Text: PropTypes.string,
  goal4Date: PropTypes.number,
  goal4IsCompleted: PropTypes.bool,
  goal4Text: PropTypes.string,
  goal5Date: PropTypes.number,
  goal5IsCompleted: PropTypes.bool,
  goal5Text: PropTypes.string,
  goalsAreCompleted: PropTypes.bool,
  goalsIsInDraft: PropTypes.bool,
  implementationApproachIsCompleted: PropTypes.bool,
  isFetching: PropTypes.bool,
  isSingleSite: PropTypes.bool,
  kickoffHosted: PropTypes.bool,
  kickoffPrepared: PropTypes.bool,
  lessonCompletionGoalValue: PropTypes.number,
  linkDropdownDisplayTitle: PropTypes.string,
  linkMenu: PropTypes.array,
  navigationEvent: PropTypes.func,
  panelData: PropTypes.array,
  playbookData: PropTypes.array,
  programStartDate: PropTypes.number,
  programStartDateIsCompleted: PropTypes.bool,
  purposeIsCompleted: PropTypes.bool,
  purposeIsInDraft: PropTypes.bool,
  showMonitorViewOnly: PropTypes.bool,
  siteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  stateDropdownOption: PropTypes.object,
  userEmail: PropTypes.string,
  userInvitationMethod: PropTypes.string,
}

export default PlaybookSteps
